import * as clack from "@clack/prompts";
import pc from "picocolors";
import { trackCommand } from "../../telemetry/events.js";
import type { SelfhostEnvOptions } from "../../types/index.js";
import { reconcileSelfhostApiUrl } from "../../utils/selfhost/api-url.js";
import {
  detectEmailStack,
  domainFromUrl,
  resolveAuthEmailFrom,
} from "../../utils/selfhost/email-stack.js";
import { validateAWSCredentials } from "../../utils/shared/aws.js";
import { isJsonMode, jsonSuccess } from "../../utils/shared/json-output.js";
import { loadConnectionMetadata } from "../../utils/shared/metadata.js";
import { resolveRegionForCommand } from "../../utils/shared/region-resolver.js";

/**
 * selfhost env — print all environment variables needed to deploy apps/web
 * to Vercel against this self-hosted control plane. Output is .env-compatible
 * so it can be piped to a file or pasted into the Vercel dashboard.
 */
export async function selfhostEnv(options: SelfhostEnvOptions): Promise<void> {
  const startTime = Date.now();

  if (!isJsonMode()) {
    clack.intro(pc.bold("Wraps Self-Hosted — Vercel Environment Variables"));
  }

  // 1. Validate AWS credentials
  const identity = await validateAWSCredentials();

  // 2. Resolve region
  const region = await resolveRegionForCommand({
    accountId: identity.accountId,
    optionRegion: options.region,
    service: "selfhost",
    label: "self-hosted deployment",
  });

  // 3. Load metadata
  const metadata = await loadConnectionMetadata(identity.accountId, region);

  if (!metadata?.services?.selfhost) {
    clack.log.error("No self-hosted deployment found");
    console.log(
      `\nRun ${pc.cyan("pnpm selfhost:deploy")} from your fork to deploy the self-hosted control plane.\n`
    );
    process.exit(1);
    return;
  }

  const { config } = metadata.services.selfhost;
  const apiUrl = await reconcileSelfhostApiUrl(metadata, region);

  if (!apiUrl) {
    clack.log.error(
      "Self-hosted deployment is incomplete — API URL is not available yet."
    );
    console.log(
      `\nThe deployment may have failed partway through. Run ${pc.cyan("pnpm selfhost:upgrade")} from your fork to complete it.\n`
    );
    process.exit(1);
    return;
  }

  const env: Record<string, string> = {
    DATABASE_URL: config.databaseUrl,
    NEXT_PUBLIC_APP_URL: config.appUrl,
    NEXT_PUBLIC_API_URL: apiUrl,
    CORS_ORIGIN: config.appUrl,
    BETTER_AUTH_SECRET: config.betterAuthSecret,
    UNSUBSCRIBE_SECRET: config.unsubscribeSecret,
    WRAPS_LICENSE_KEY: config.licenseKey,
    AWS_BACKEND_ACCOUNT_ID: identity.accountId,
  };

  // Auth email config. This command is the ONLY channel by which the Pulumi
  // variant's operator learns these — it deploys no dashboard, so apps/web is
  // hosted by them. Omitting these left signup verification, invitations and
  // password reset unconfigured on every API-only self-hosted install.
  const emailStack = await detectEmailStack(region);
  const authEmailFrom = resolveAuthEmailFrom({
    webDomain: domainFromUrl(config.appUrl),
    verifiedDomains: emailStack.verifiedDomains,
  });

  if (authEmailFrom) {
    env.AUTH_EMAIL_FROM = authEmailFrom;
  }
  if (emailStack.configSetName) {
    env.AUTH_EMAIL_CONFIGURATION_SET = emailStack.configSetName;
  }
  // Kept for this variant, unlike the SST one that had it removed: the target
  // here is apps/web on Vercel, where getWrapsClient() exchanges Vercel's OIDC
  // token for the role. That path the email role's trust policy does allow —
  // what it refuses is a plain sts:AssumeRole from a Lambda in-account.
  if (emailStack.roleArn) {
    env.WRAPS_EMAIL_ROLE_ARN = emailStack.roleArn;
  }

  if (isJsonMode()) {
    jsonSuccess("selfhost.env", { env });
    return;
  }

  // Output in .env format — no ANSI so it can be piped to a file
  console.log("# Wraps Self-Hosted — environment for apps/web on Vercel");
  console.log(
    `# Deployment: ${identity.accountId} / ${region} — ${config.appUrl}`
  );
  console.log(`# Generated: ${new Date().toISOString()}`);
  console.log("");

  for (const [key, value] of Object.entries(env)) {
    console.log(`${key}=${value}`);
  }

  console.log("");
  console.log(
    "# ============================================================================="
  );
  console.log("# AWS Backend Credentials — Vercel OIDC (recommended)");
  console.log(
    "# ============================================================================="
  );
  console.log("#");
  console.log("# 1. In AWS IAM → Identity providers → Add provider:");
  console.log("#    Provider type: OpenID Connect");
  console.log("#    Provider URL:  https://oidc.vercel.com/<team-slug>");
  console.log("#    Audience:      https://vercel.com/<team-slug>");
  console.log("#");
  console.log(
    "# 3. Create an IAM role in this account with the following trust policy,"
  );
  console.log("#    replacing <team-slug> with your Vercel team slug:");
  console.log("#");
  console.log("#    {");
  console.log(`#      "Version": "2012-10-17",`);
  console.log(`#      "Statement": [{`);
  console.log(`#        "Effect": "Allow",`);
  console.log(`#        "Principal": {`);
  console.log(
    `#          "Federated": "arn:aws:iam::${identity.accountId}:oidc-provider/oidc.vercel.com/<team-slug>"`
  );
  console.log("#        },");
  console.log(`#        "Action": "sts:AssumeRoleWithWebIdentity",`);
  console.log(`#        "Condition": {`);
  console.log(`#          "StringEquals": {`);
  console.log(
    `#            "oidc.vercel.com/<team-slug>:aud": "https://vercel.com/<team-slug>",`
  );
  console.log(
    `#            "oidc.vercel.com/<team-slug>:sub": "owner:<team-slug>:project:<project-name>:environment:production"`
  );
  console.log("#          }");
  console.log("#        }");
  console.log("#      }]");
  console.log("#    }");
  console.log("#");
  console.log("#    Attach this permission policy to the role:");
  console.log("#");
  console.log("#    {");
  console.log(`#      "Version": "2012-10-17",`);
  console.log(`#      "Statement": [{`);
  console.log(`#        "Effect": "Allow",`);
  console.log(`#        "Action": "sts:AssumeRole",`);
  console.log(`#        "Resource": [`);
  console.log(
    `#          "arn:aws:iam::*:role/wraps-selfhost-console-access-role",`
  );
  console.log(`#          "arn:aws:iam::*:role/wraps-console-access-role"`);
  console.log("#        ]");
  console.log("#      }]");
  console.log("#    }");
  console.log("#");
  console.log("# 4. Set AWS_ROLE_ARN to that role's ARN in Vercel:");
  console.log(
    `# AWS_ROLE_ARN=arn:aws:iam::${identity.accountId}:role/<your-vercel-backend-role>`
  );

  clack.outro(
    pc.dim(
      "Paste into Vercel → Settings → Environment Variables → Add from .env"
    )
  );

  trackCommand("selfhost:env", {
    success: true,
    duration_ms: Date.now() - startTime,
  });
}
